import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp,
  MapPin,
  Home,
  DollarSign,
  Percent,
  Users,
  Clock,
  ArrowLeft,
  RefreshCw,
  Bookmark,
  BookmarkCheck,
  AlertCircle
} from 'lucide-react';
import API_CONSTRUCTOR from '../../models/api-model';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  getPropertyLocation,
  getFundingProgressColor
} from '../../utils/investorUtils';
import { toast } from 'sonner';
import HouseFlippingCalculator from '../../components/calculator';
import CommitmentDialog from '../../components/investor/CommitmentDialog';
import { getPropertyData, getPropertyField } from '../../utils/propertyDataHelper';

export const OpportunityDetail = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCommitDialog, setShowCommitDialog] = useState(false);

  // Load opportunity details
  const loadOpportunity = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const api = API_CONSTRUCTOR();
      const response = await api.get(`/opportunities/${queryId}`);
      
      // The backend returns { query, commitments, activeInvestors, userCommitment }
      // Extract the query object which contains all calculation fields
      setOpportunity(response.data.query);
    } catch (err) {
      console.error('Error loading opportunity:', err);
      setError(err.response?.data?.error || 'Failed to load opportunity');
      toast.error('Failed to load opportunity details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOpportunity();
  }, [queryId]);

  // Handle commit button click
  const handleCommit = () => {
    setShowCommitDialog(true);
  };

  // Handle successful commitment
  const handleCommitmentSuccess = () => {
    setShowCommitDialog(false);
    loadOpportunity(); // Reload to get updated funding status
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/opportunities')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Opportunities
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <p className="text-red-600 text-center">{error || 'Opportunity not found'}</p>
            <div className="text-center mt-4">
              <Button onClick={loadOpportunity}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fundingPercentage = opportunity.funding_percentage || 0;
  const remainingPercentage = 100 - fundingPercentage;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/opportunities')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Opportunities
        </Button>
        
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Available for Investment
              </Badge>
              {opportunity.investor_count > 0 && (
                <Badge variant="outline">
                  {opportunity.investor_count} Investor{opportunity.investor_count !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {opportunity.property_address || 'Property Address'}
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {getPropertyLocation(opportunity)}
            </p>
          </div>
          
          <Bookmark className="h-6 w-6 text-gray-400 hover:text-primary cursor-pointer" />
        </div>
      </div>

      {/* Investment Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Investment Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Investment Base</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(opportunity.total_investment_base)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Expected Sale Price</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(opportunity.estimated_sale_price)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Profit Pool</p>
              <p className="text-2xl font-bold">{formatCurrency(opportunity.total_profit)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Investor Profit Share</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(opportunity.investor_profit_total)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t">
            <div className="flex items-center gap-3">
              <Percent className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-muted-foreground">Return Rate</p>
                <p className="text-lg font-semibold">{opportunity.return_rate}% per annum</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-muted-foreground">Investment Period</p>
                <p className="text-lg font-semibold">{opportunity.rental_period} months</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Home className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-muted-foreground">Homeowner</p>
                <p className="text-lg font-semibold">{opportunity.homeowner_name || 'N/A'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Funding Progress */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Funding Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Current Funding</span>
              <span className="font-semibold">{formatPercentage(fundingPercentage)} of 100%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full transition-all ${getFundingProgressColor(fundingPercentage)}`}
                style={{ width: `${Math.min(fundingPercentage, 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Funded</p>
              <p className="text-lg font-bold">{formatPercentage(fundingPercentage)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Remaining</p>
              <p className="text-lg font-bold">{formatPercentage(remainingPercentage)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Investors</p>
              <p className="text-lg font-bold flex items-center gap-1">
                <Users className="h-4 w-4" />
                {opportunity.investor_count || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Min Investment</p>
              <p className="text-lg font-bold">5%</p>
            </div>
          </div>

          {remainingPercentage > 0 && (
            <div className="mt-6">
              <Button onClick={handleCommit} size="lg" className="w-full">
                Commit to Invest
              </Button>
            </div>
          )}

          {remainingPercentage === 0 && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-center font-medium">
                This opportunity is fully funded!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Investment Notes */}
      {opportunity.notes_for_investor && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Notes for Investors</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{opportunity.notes_for_investor}</p>
          </CardContent>
        </Card>
      )}

      {/* Full Calculator in Read-Only Mode */}
      {opportunity?.calculator_inputs && (
        <div className="mb-6">
          <HouseFlippingCalculator
            buildingSquareFeet={opportunity.calculator_inputs?.homeArea || 1500}
            estimatedRentAmount={opportunity.calculator_inputs?.currentRental || 1800}
            estimatedValue={opportunity.calculator_inputs?.presentValue || 700000}
            loanBalance={opportunity.calculator_inputs?.loanAmount || 0}
            lotSquareFeet={opportunity.calculator_inputs?.lotSize || 5000}
            pool={opportunity.calculator_inputs?.hasPool === 'yes'}
            readOnly={true}
            initialInputs={opportunity.calculator_inputs}
          />
        </div>
      )}

      {/* Property Details */}
      {(() => {
        const propertyData = getPropertyData(opportunity);
        const bedrooms = getPropertyField(opportunity, 'bedrooms');
        const bathrooms = getPropertyField(opportunity, 'bathrooms');
        const squareFeet = getPropertyField(opportunity, 'building_square_feet') || getPropertyField(opportunity, 'squareFeet');
        const yearBuilt = getPropertyField(opportunity, 'year_built') || getPropertyField(opportunity, 'yearBuilt');
        const lotSize = getPropertyField(opportunity, 'lot_square_feet') || getPropertyField(opportunity, 'lotSize');
        const propertyType = getPropertyField(opportunity, 'property_type') || getPropertyField(opportunity, 'propertyType');
        
        const hasAnyDetails = bedrooms || bathrooms || squareFeet || yearBuilt || lotSize || propertyType;
        
        if (!hasAnyDetails) return null;
        
        return (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {bedrooms && (
                  <div>
                    <p className="text-sm text-muted-foreground">Bedrooms</p>
                    <p className="font-medium">{bedrooms}</p>
                  </div>
                )}
                {bathrooms && (
                  <div>
                    <p className="text-sm text-muted-foreground">Bathrooms</p>
                    <p className="font-medium">{bathrooms}</p>
                  </div>
                )}
                {squareFeet && (
                  <div>
                    <p className="text-sm text-muted-foreground">Square Feet</p>
                    <p className="font-medium">{Number(squareFeet).toLocaleString()} sq ft</p>
                  </div>
                )}
                {yearBuilt && (
                  <div>
                    <p className="text-sm text-muted-foreground">Year Built</p>
                    <p className="font-medium">{yearBuilt}</p>
                  </div>
                )}
                {lotSize && (
                  <div>
                    <p className="text-sm text-muted-foreground">Lot Size</p>
                    <p className="font-medium">{Number(lotSize).toLocaleString()} sq ft</p>
                  </div>
                )}
                {propertyType && (
                  <div>
                    <p className="text-sm text-muted-foreground">Property Type</p>
                    <p className="font-medium">{propertyType}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Commitment Dialog */}
      <CommitmentDialog
        open={showCommitDialog}
        onClose={() => setShowCommitDialog(false)}
        opportunity={opportunity}
        onSuccess={handleCommitmentSuccess}
      />
    </div>
  );
};

export default OpportunityDetail;
